import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { CabecalhoPagina, FaixaFiltros } from '../components/layout/CabecalhoPagina';
import { Dica, BarraAcoes, CabecalhoSecao } from '../components/common/Formulario';
import { athleteApi, teamApi, federationApi, beltApi, fileApi, userApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { filterBeltsForBirthDate, beltAgeRangeText } from '../lib/beltRules';
// FASE E — documento em UM CAMPO SÓ (CPF ou RG): máscara, validação e avisos
import {
  sameCpfWarning, GUARDIAN_RELATIONSHIPS,
  documentoDoRegistro, tipoDoRegistro, documentoParaCampos, comCamposDeDocumento,
} from '../lib/documentos';
import CampoDocumento from '../components/CampoDocumento';
import CampoDocumentoInteligente from '../components/CampoDocumentoInteligente';
// FASE 1 — nacionalidade (com sugestões de países) e naturalidade
import CampoPais from '../components/CampoPais';
import { normalizarNaturalidade } from '../lib/paises';
// FASE 3 — filtro "sem graduar há X meses" (o limite é escolha do usuário)
import { semGraduarHa, FILTROS_SEM_GRADUAR } from '../lib/graduacao';
// FASE 2 — data da graduação ("faixa atual desde")
import CampoDataGraduacao from '../components/CampoDataGraduacao';
// FASE 3 — histórico de graduação (linha do tempo + registrar)
import HistoricoGraduacao from '../components/HistoricoGraduacao';
// FASE 4 — relatório de completude do cadastro
import CompletudeCadastro from '../components/CompletudeCadastro';
import { ImageUpload } from '../components/common/ImageUpload';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Combobox } from '../components/ui/combobox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { UserCircle, Plus, Search, Loader2, Edit, Trash2, CreditCard, ChevronDown, MapPin, Users, GraduationCap, Heart, Stethoscope, Briefcase, ArrowRightLeft, History, ChevronLeft, ChevronRight, Printer, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import TransferAthleteDialog from '../components/TransferAthleteDialog';
import MigrationHistoryDialog from '../components/MigrationHistoryDialog';
import DialogoMotivo from '../components/common/DialogoMotivo';
import { DialogoDuplicata } from '../components/DialogoDuplicata';
import { DialogoMergeAtleta } from '../components/DialogoMergeAtleta';
import { EstadoVazio } from '../components/common/Estados';

// Reusable collapsible section used by the athlete form (Wave 2)
const CollapsibleSection = ({ icon, title, testid, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="border border-[#27272A] rounded-lg bg-[#0F0F0F]">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#1A1A1A] rounded-lg"
        >
          {/* Título de seção CLICÁVEL: mesma escala do `.rotulo-secao` (12px,
              tracking 0.06em), mas em dourado — aqui a cor sinaliza que a
              seção abre e fecha, enquanto o título estático é branco. */}
          <span className="text-xs font-bold text-[#D4AF37] uppercase tracking-[0.06em] flex items-center gap-2">
            {icon} {title}
          </span>
          <ChevronDown size={16} className={`text-[#71717A] transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4 pt-1">{children}</CollapsibleContent>
    </Collapsible>
  );
};

export default function AthletesPage() {
  const { user } = useAuth();
  const isProfessor = user?.role === 'professor';
  const isAthlete = user?.role === 'athlete';
  const canManage = ['super_admin', 'federation', 'professor', 'team'].includes(user?.role);
  // Per-athlete ownership gate for Edit (and any "own scope" action).
  // - super_admin / federation: always
  // - everyone else: allowed if they are the athlete's chosen supervisor
  //   (athlete.professor_id == user._id) OR the president of the athlete's team
  //   (team.president_id == user._id). Matches backend rule.
  const canManageAthlete = (athlete) => {
    if (!athlete) return false;
    if (['super_admin', 'federation'].includes(user?.role)) return true;
    if (athlete.professor_id && athlete.professor_id === user?._id) return true;
    if (athlete.team_id) {
      const team = teams.find(t => t._id === athlete.team_id);
      if (team && team.president_id === user?._id) return true;
    }
    return false;
  };
  // Delete is restricted to super_admin / federation by backend
  const canDeleteAthlete = ['super_admin', 'federation'].includes(user?.role);
  const [athletes, setAthletes] = useState([]);
  const [teams, setTeams] = useState([]);
  const [professors, setProfessors] = useState([]);
  const [federations, setFederations] = useState([]);
  const [belts, setBelts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [beltFilter, setBeltFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  // FASE 3 — filtro "sem graduar há X meses" (0 = não filtra)
  const [mesesSemGraduar, setMesesSemGraduar] = useState(0);
  // FASE 4 — relatório de completude do cadastro
  const [completudeOpen, setCompletudeOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAthlete, setEditingAthlete] = useState(null);

  /* PASSO 2 (duplicidade) — quando o backend recusa por encontrar um atleta
     parecido (409), ele NÃO salvou nada. Aqui ficam a lista de parecidos e os
     dados que a pessoa digitou, para o diálogo decidir entre abrir o cadastro
     existente ("é este") ou criar assim mesmo ("é outro atleta"). */
  const [duplicatas, setDuplicatas] = useState(null);
  const [dadosPendentes, setDadosPendentes] = useState(null);
  /* PASSO 4 — a tela de unificação de cadastros duplicados. */
  const [mergeAberto, setMergeAberto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const [transferAthlete, setTransferAthlete] = useState(null);
  const [historyAthlete, setHistoryAthlete] = useState(null);
  // Fase 2 (item 7): atleta aguardando o motivo da rejeição no diálogo.
  const [rejeitando, setRejeitando] = useState(null);
  const navigate = useNavigate();

  // Who can transfer this athlete?
  // - super_admin / federation: always
  // - team (president): yes (limited to changing professor inside team — enforced by backend)
  // - everyone else: no
  const canTransfer = ['super_admin', 'federation', 'team'].includes(user?.role);
  // Who can see history?
  // - super_admin / federation: always
  // - professor: only their own athletes
  const canSeeHistory = (athlete) => {
    if (['super_admin', 'federation', 'team'].includes(user?.role)) return true;
    if (user?.role === 'professor') return athlete.professor_id === user?._id;
    return false;
  };

  // Empty template — single source of truth for all athlete form fields
  const emptyForm = () => ({
    // Basic
    name: '', birth_date: '', sex: 'M',
    weight: '', belt: '', team_id: '', professor_id: '', federation_ids: [],
    phone: '', email: '', photo_path: '',
    // FASE E — documento em UM CAMPO SÓ (CPF ou RG), inteligente e OPCIONAL,
    // mais o responsável legal (também opcional).
    documento: '', documento_tipo: 'auto',
    guardian_name: '', guardian_relationship: '', guardian_cpf: '',
    // FASE 1 — nacionalidade e naturalidade (opcionais; NÃO nascem preenchidos:
    // quem escolher "Brasil" na lista grava a grafia certa)
    nationality: '', birth_place: '',
    // FASE 2 — data da graduação (opcional): desde quando tem a faixa atual
    belt_since: '',
    // Start
    start_date: '',
    // Responsáveis
    father_name: '', mother_name: '',
    home_phone: '', responsible_cell_phone: '',
    // Endereço
    address: '', neighborhood: '', city: '', state: '', zip_code: '',
    // Escola
    education_level: '', school_name: '', school_grade: '', school_shift: '',
    // Saúde
    has_special_needs: false, special_needs_description: '',
    has_health_issues: false, health_issues_description: '',
    // Médico
    blood_type: '', height_cm: '',
    medical_certificate_date: '', doctor_name: '', doctor_crm: '',
  });

  const [formData, setFormData] = useState(emptyForm());

  const fetchData = async () => {
    try {
      const [athletesRes, teamsRes, fedRes, beltsRes, profRes] = await Promise.all([
        athleteApi.list(),
        teamApi.list(), federationApi.list(), beltApi.list(),
        userApi.list({ role: 'professor' })
      ]);
      setAthletes(athletesRes.data);
      setTeams(teamsRes.data);
      setFederations(fedRes.data);
      setBelts(beltsRes.data);
      setProfessors(profRes.data || []);
      if (beltsRes.data.length > 0 && !formData.belt) {
        setFormData(prev => ({ ...prev, belt: beltsRes.data[0].name }));
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // REGRA 06/10 (decisão do usuário): o professor ABRE vendo SÓ os alunos dele.
  // Ao escolher uma equipe no filtro, ele passa a ver AQUELA equipe — uma por vez,
  // inclusive de outra equipe — e o botão [Meus alunos] aparece para voltar.
  // Não precisa de estado novo: a visão é derivada do próprio filtro de equipe.
  const filtrandoPorEquipe = teamFilter !== 'all';
  const mostrarSoMeusAlunos = isProfessor && !filtrandoPorEquipe;

  // Recarrega os atletas. COM `teamId`, o SERVIDOR devolve aquela equipe (é a
  // liberação pedida); SEM ele, volta o conjunto padrão (alunos dele + equipes).
  const carregarAtletas = useCallback(async (teamId) => {
    try {
      const { data } = await athleteApi.list(teamId ? { team_id: teamId } : undefined);
      setAthletes(data);
    } catch (error) {
      toast.error('Erro ao carregar atletas');
    }
  }, []);

  // Resolve the professor's "home team" using the new hierarchy:
  // priority 1: team where user is president; priority 2: team where user is in professor_ids[]; priority 3: legacy user.team_id
  const resolveMyTeamId = useCallback(() => {
    if (!isProfessor || !user?._id) return '';
    const myId = user._id;
    const asPresident = teams.find(t => t.president_id === myId);
    if (asPresident) return asPresident._id;
    const asMember = teams.find(t => Array.isArray(t.professor_ids) && t.professor_ids.includes(myId));
    if (asMember) return asMember._id;
    return user?.team_id || '';
  }, [isProfessor, user, teams]);

  const resetForm = () => {
    setFormData({
      ...emptyForm(),
      belt: belts.length > 0 ? belts[0].name : '',
      team_id: resolveMyTeamId(),
      professor_id: isProfessor ? (user?._id || '') : '',
    });
    setEditingAthlete(null);
    setPendingPhoto(null);
  };

  const handleEdit = (athlete) => {
    setFormData({
      ...emptyForm(),
      // overlay every known field if present on the document
      ...Object.fromEntries(
        Object.entries(athlete).filter(([k]) => k in emptyForm())
      ),
      // FASE E — o campo único abre com o que já está gravado (CPF, RG ou o
      // valor do campo antigo `document`), sem perder nada.
      documento: documentoDoRegistro(athlete),
      documento_tipo: tipoDoRegistro(athlete),
      // numeric fields => string for input
      weight: athlete.weight?.toString() ?? '',
      height_cm: athlete.height_cm?.toString() ?? '',
      federation_ids: athlete.federation_ids || [],
      photo_path: athlete.photo_path || '',
    });
    setEditingAthlete(athlete);
    setPendingPhoto(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este atleta?')) return;
    try {
      await athleteApi.delete(id);
      toast.success('Atleta excluido');
      fetchData();
    } catch (error) { toast.error('Erro ao excluir atleta'); }
  };

  const handlePhotoUpload = (pathOrFile) => {
    if (pathOrFile instanceof File) setPendingPhoto(pathOrFile);
    else setFormData(prev => ({ ...prev, photo_path: pathOrFile }));
  };

  /* Monta o payload do formulário (FASE E: o campo único de documento vira
     `document` + `cpf` ou `rg` no envio). */
  const montarDadosAtleta = () => ({
    ...comCamposDeDocumento(formData),
    weight: parseFloat(formData.weight) || 0,
    height_cm: formData.height_cm === '' ? null : parseFloat(formData.height_cm),
  });

  /* Grava de verdade. `confirmarDuplicata` só chega verdadeiro depois que a
     pessoa respondeu "é outro atleta" no diálogo do passo 2. */
  const salvarAtleta = async (data, confirmarDuplicata = false) => {
    let result;
    if (editingAthlete) {
      const { data: updated } = await athleteApi.update(editingAthlete._id, data);
      result = { _id: editingAthlete._id };
      toast.success('Atleta atualizado');
      // FASE E — avisos de CPF (inválido / repetido / igual ao do responsável).
      // São AVISOS: o cadastro foi salvo normalmente.
      (updated?.warnings || []).forEach((w) => toast.warning(w));
    } else {
      const { data: newAthlete } = await athleteApi.create(
        data,
        confirmarDuplicata ? { confirmar_duplicata: true } : undefined,
      );
      result = newAthlete;
      toast.success('Atleta criado');
      (newAthlete?.warnings || []).forEach((w) => toast.warning(w));
    }
    if (pendingPhoto && result._id) {
      try { await fileApi.upload(pendingPhoto, 'athlete', result._id); }
      catch (err) { toast.error('Erro ao enviar foto'); }
    }
    setIsDialogOpen(false);
    resetForm();
    fetchData();
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSaving(true);
    const data = montarDadosAtleta();
    try {
      await salvarAtleta(data);
    } catch (error) {
      const detalhe = error?.response?.data?.detail;
      // PASSO 2 — o backend não criou nada: achou um atleta parecido e devolveu
      // a lista. Em vez de um toast de erro, abre a PERGUNTA.
      if (error?.response?.status === 409 && detalhe?.atletas?.length) {
        setDadosPendentes(data);
        setDuplicatas(detalhe.atletas);
        return;
      }
      const fallback = editingAthlete ? 'Erro ao atualizar' : 'Erro ao criar';
      toast.error(typeof detalhe === 'string' ? detalhe : fallback);
    } finally { setSaving(false); }
  };

  /* "É outro atleta" — reenvia confirmando; o cadastro é criado normalmente. */
  const confirmarCriacaoDuplicata = async () => {
    if (!dadosPendentes) return;
    setSaving(true);
    try {
      await salvarAtleta(dadosPendentes, true);
      setDuplicatas(null);
      setDadosPendentes(null);
    } catch (error) {
      const detalhe = error?.response?.data?.detail;
      toast.error(typeof detalhe === 'string' ? detalhe : 'Erro ao criar');
    } finally { setSaving(false); }
  };

  /* "É este" — NÃO cria nada: abre o cadastro que já existe, para a pessoa
     completar ou inscrever quem já estava na base. */
  const usarAtletaExistente = async (resumo) => {
    try {
      const { data: completo } = await athleteApi.get(resumo._id);
      setDuplicatas(null);
      setDadosPendentes(null);
      handleEdit(completo);
      toast.info(`Abrindo o cadastro existente de ${completo?.name || resumo.name}`);
    } catch {
      toast.error('Erro ao abrir o cadastro existente');
    }
  };

  const cancelarDuplicata = () => {
    setDuplicatas(null);
    setDadosPendentes(null);
  };

  // Professors filtered by currently-selected team (drives the Professor dropdown)
  const teamProfessorIds = useMemo(() => {
    const team = teams.find(t => t._id === formData.team_id);
    if (!team) return null; // null = show all professors
    const ids = new Set(team.professor_ids || []);
    if (team.president_id) ids.add(team.president_id);
    if (team.professor_id) ids.add(team.professor_id); // legacy
    (team.monitors || []).forEach(m => ids.add(m));    // legacy
    return ids;
  }, [formData.team_id, teams]);

  const filteredProfessorsForForm = useMemo(() => {
    if (!teamProfessorIds || teamProfessorIds.size === 0) return professors;
    return professors.filter(p => teamProfessorIds.has(p._id));
  }, [professors, teamProfessorIds]);

  const filteredAthletes = athletes.filter(a => {
    const matchesSearch = a.name.toLowerCase().includes(search.toLowerCase());
    const matchesBelt = beltFilter === 'all' || a.belt === beltFilter;
    const matchesTeam = teamFilter === 'all' || a.team_id === teamFilter;
    const matchesStatus = statusFilter === 'all' || (a.status || 'approved') === statusFilter;
    // FASE 3 — "sem graduar há X meses": o LIMITE é escolhido aqui, não é regra
    // do sistema (cada equipe gradua no seu tempo). Quem não tem data entra.
    const matchesGraduacao = semGraduarHa(a.belt_since, mesesSemGraduar);
    // REGRA 06/10: por padrão o professor vê SÓ os alunos dele — o MESMO critério
    // do rótulo "SOB MINHA SUPERVISÃO" e do direito de editar. Ao filtrar por uma
    // equipe, a lista vem do servidor já restrita àquela equipe.
    const matchesEscopo = !mostrarSoMeusAlunos || a.professor_id === user?._id;
    return matchesSearch && matchesBelt && matchesTeam && matchesStatus
      && matchesGraduacao && matchesEscopo;
  });

  // ---------- Client-side pagination ----------
  const totalPages = Math.max(1, Math.ceil(filteredAthletes.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = filteredAthletes.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, filteredAthletes.length);
  const pagedAthletes = filteredAthletes.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const renderPaginationBar = (showPageSize) => (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-3">
      <div className="flex flex-wrap items-center gap-3 text-sm text-[#A1A1AA]">
        {showPageSize && (
          <>
            <span className="text-xs uppercase tracking-wider text-[#71717A]">Exibir por pagina</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger data-testid="page-size-select" className="bg-[#1A1A1A] border-[#27272A] text-white h-8 w-[76px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                {[25, 50, 75, 100].map((n) => (
                  <SelectItem key={n} value={String(n)} className="text-white hover:bg-[#27272A]">{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
        <span>Mostrando <span className="text-white font-semibold">{start}-{end}</span> de <span className="text-white font-semibold">{filteredAthletes.length}</span> atletas</span>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="prev-page" aria-label="Página anterior" className="border-[#27272A] text-white">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="px-2 text-sm text-[#A1A1AA]">Pagina <span className="text-white font-semibold">{currentPage}</span> / {totalPages}</span>
        <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="next-page" aria-label="Próxima página" className="border-[#27272A] text-white">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  // Approval permission: super_admin OR chosen supervisor OR team president
  const canApproveAthlete = (a) => {
    if (!a || (a.status || 'approved') !== 'pending') return false;
    if (user?.role === 'super_admin') return true;
    if (a.professor_id && a.professor_id === user?._id) return true;
    if (a.team_id) {
      const team = teams.find(t => t._id === a.team_id);
      if (team && team.president_id === user?._id) return true;
    }
    return false;
  };

  const handleApprove = async (id) => {
    try {
      await athleteApi.approve(id);
      toast.success('Atleta aprovado!');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao aprovar');
    }
  };
  // Fase 2 (item 7): o motivo passou a ser pedido num diálogo do sistema, mas o
  // comportamento é EXATAMENTE o de antes (decisão do cliente): qualquer forma
  // de fechar o diálogo confirma a rejeição e o motivo continua opcional — era
  // isso que o `window.prompt(...) || ''` fazia.
  const handleReject = (id) => setRejeitando(id);

  const aplicarRejeicao = async (id, reason) => {
    setRejeitando(null);
    try {
      await athleteApi.reject(id, reason);
      toast.info('Atleta rejeitado');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao rejeitar');
    }
  };

  const getTeamName = (teamId) => teams.find(t => t._id === teamId)?.name || '-';

  // FASE 3 — depois de registrar/apagar uma graduação, o SERVIDOR recalcula a
  // faixa atual do atleta. Aqui a ficha só se alinha com o que voltou (inclusive
  // a faixa "original", para não aparecer a pergunta de troca de faixa à toa).
  const handleGraduacaoMudou = (data) => {
    const faixa = data?.athlete_belt || formData.belt;
    const desde = data?.athlete_belt_since ?? formData.belt_since;
    setFormData((prev) => ({ ...prev, belt: faixa, belt_since: desde || '' }));
    setEditingAthlete((prev) => (prev ? { ...prev, belt: faixa, belt_since: desde } : prev));
    fetchData();
  };

  // FASE E — o que o campo único de documento entendeu (CPF ou RG): usado no
  // aviso de "CPF do atleta igual ao do responsável".
  const documentoDoForm = documentoParaCampos(formData.documento, formData.documento_tipo);
  const avisoCpfResponsavel = sameCpfWarning(documentoDoForm.cpf, formData.guardian_cpf);
  const getBeltStyle = (beltName) => {
    const b = belts.find(b => b.name === beltName);
    if (!b) return {};
    const isLight = ['#FFFFFF', '#EAB308', '#9CA3AF'].includes(b.color_code);
    return { backgroundColor: b.color_code, color: isLight ? '#000' : '#fff', border: b.color_code === '#000000' ? '1px solid #555' : 'none' };
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="athletes-page">
        {/* Header — título/contexto à esquerda, ações à direita */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <CabecalhoPagina
            className="min-w-0 flex-1"
            contexto="Cadastros"
            titulo="Atletas"
            descricao="Gerencie os atletas cadastrados"
            icone={UserCircle}
          />
          <div className="flex items-center gap-2">
            {/* FASE 4 — relatório de completude do cadastro */}
            {canManage && (
              <Button
                variant="outline"
                onClick={() => setCompletudeOpen(true)}
                data-testid="completude-btn"
                className="border-[#27272A] text-[#A1A1AA] hover:text-white"
              >
                <ClipboardList size={16} className="mr-2" /> Completude do cadastro
              </Button>
            )}
            {/* PASSO 4 — manter a base limpa: procura cadastros que parecem ser a
                mesma pessoa e unifica (nada é apagado). */}
            {canManage && (
              <Button
                variant="outline"
                onClick={() => setMergeAberto(true)}
                data-testid="duplicados-btn"
                className="border-[#27272A] text-[#A1A1AA] hover:text-white"
              >
                <Users size={16} className="mr-2" /> Duplicados
              </Button>
            )}
            {/* PASSO 2 (duplicidade) — a pergunta que substitui o antigo toast:
            quando o backend encontra alguém parecido ele NÃO grava, e é aqui
            que a pessoa decide entre usar o cadastro existente ou criar novo. */}
        <DialogoDuplicata
          aberto={!!duplicatas}
          atletas={duplicatas || []}
          nomeNovo={formData.name}
          aoUsarExistente={usarAtletaExistente}
          aoCriarNovo={confirmarCriacaoDuplicata}
          aoCancelar={cancelarDuplicata}
          processando={saving}
        />

        <DialogoMergeAtleta
          aberto={mergeAberto}
          aoFechar={() => setMergeAberto(false)}
          aoConcluir={fetchData}
        />

        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            {canManage && (
              <DialogTrigger asChild>
                <Button data-testid="new-athlete-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold">
                  <Plus size={20} className="mr-2" /> Novo Atleta
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="bg-[#141414] border-[#27272A] w-[95vw] sm:max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0">
              <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#27272A]">
                <DialogTitle className="text-white font-['Chivo']">{editingAthlete ? 'Editar Atleta' : 'Novo Atleta'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-5" data-testid="athlete-form">

                <p className="text-xs text-[#A1A1AA]"><span className="text-[#D4AF37]">*</span> campos obrigatórios</p>

                {/* ============ SEÇÃO: IDENTIFICAÇÃO ============ */}
                <div className="space-y-3">
                  <CabecalhoSecao titulo="Identificação" icone={UserCircle} />
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="athlete-name">Nome Completo *</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })} placeholder="NOME DO ATLETA" required id="athlete-name" data-testid="athlete-name" className="bg-[#1A1A1A] border-[#27272A] text-white uppercase" />
                  </div>
                  {/* Data de Nascimento e o documento ficam LADO A LADO. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]" htmlFor="athlete-birth-date">Data de Nascimento *</Label>
                      <Input type="date" value={formData.birth_date} onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })} required id="athlete-birth-date" data-testid="athlete-birth-date" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                    </div>
                    {/* FASE E — DOCUMENTO EM UM CAMPO SÓ: ele reconhece se é CPF
                        (máscara + dígito verificador) ou RG (máscara leve). */}
                    <div data-testid="athlete-documento-block">
                      <CampoDocumentoInteligente
                        label="CPF ou RG do atleta"
                        value={formData.documento}
                        tipo={formData.documento_tipo}
                        onChange={(v) => setFormData({ ...formData, documento: v })}
                        onTipoChange={(t) => setFormData({ ...formData, documento_tipo: t })}
                        testId="athlete-documento"
                        hint="Opcional. Digite o CPF ou o RG — o campo se vira sozinho."
                      />
                    </div>
                  </div>

                  {/* FASE 1 — Nacionalidade e Naturalidade na mesma linha. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                    <CampoPais
                      value={formData.nationality}
                      onChange={(v) => setFormData({ ...formData, nationality: v })}
                      testId="athlete-nationality"
                      hint="Opcional. Comece a digitar para ver as sugestões."
                    />
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]" htmlFor="athlete-birth-place">Naturalidade</Label>
                      <Input
                        value={formData.birth_place}
                        onChange={(e) => setFormData({ ...formData, birth_place: e.target.value })}
                        onBlur={(e) => setFormData(prev => ({ ...prev, birth_place: normalizarNaturalidade(e.target.value) }))}
                        placeholder="Ex: Rio de Janeiro/RJ"
                        id="athlete-birth-place" data-testid="athlete-birth-place"
                        className="bg-[#1A1A1A] border-[#27272A] text-white"
                      />
                      <Dica icone={false}>
                        Opcional. Cidade e estado onde nasceu (como na ficha).
                      </Dica>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]" htmlFor="athlete-sex">Sexo *</Label>
                      <Select value={formData.sex} onValueChange={(v) => setFormData({ ...formData, sex: v })}>
                        <SelectTrigger id="athlete-sex" data-testid="athlete-sex" className="bg-[#1A1A1A] border-[#27272A] text-white"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                          <SelectItem value="M" className="text-white">Masculino</SelectItem>
                          <SelectItem value="F" className="text-white">Feminino</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]" htmlFor="athlete-weight">Peso (kg) *</Label>
                      <Input type="number" step="0.1" value={formData.weight} onChange={(e) => setFormData({ ...formData, weight: e.target.value })} placeholder="70.5" required id="athlete-weight" data-testid="athlete-weight" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]" htmlFor="athlete-belt">Faixa *</Label>
                      <Select value={formData.belt || "none"} onValueChange={(v) => setFormData({ ...formData, belt: v === "none" ? "" : v })}>
                        <SelectTrigger id="athlete-belt" data-testid="athlete-belt" className="bg-[#1A1A1A] border-[#27272A] text-white"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                          <SelectItem value="none" className="text-white">Selecione...</SelectItem>
                          {filterBeltsForBirthDate(belts, formData.birth_date).map((b) => (
                            <SelectItem key={b._id} value={b.name} className="text-white hover:bg-[#27272A]">
                              <span className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: b.color_code, border: b.color_code === '#000000' ? '1px solid #555' : b.color_code === '#FFFFFF' ? '1px solid #999' : 'none' }} />
                                {b.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.belt && beltAgeRangeText(formData.belt) && (
                        <Dica icone={false} testid="athlete-belt-age-hint">
                          {beltAgeRangeText(formData.belt)} (regra CBJJ/IBJJF)
                        </Dica>
                      )}
                    </div>
                  </div>

                  {/* FASE 2 — desde quando o atleta tem a faixa atual (opcional).
                      Se a faixa for trocada aqui, o campo PERGUNTA se a data de
                      hoje deve virar a nova "desde quando" — nunca muda sozinho. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                    <CampoDataGraduacao
                      value={formData.belt_since}
                      onChange={(v) => setFormData({ ...formData, belt_since: v })}
                      belt={formData.belt}
                      beltOriginal={editingAthlete?.belt || ''}
                      startDate={formData.start_date}
                      birthDate={formData.birth_date}
                      testId="athlete-belt-since"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]" htmlFor="athlete-team">Equipe</Label>
                      <Select value={formData.team_id || "none"} onValueChange={(v) => setFormData({ ...formData, team_id: v === "none" ? "" : v, professor_id: '' })}>
                        <SelectTrigger id="athlete-team" data-testid="athlete-team" className="bg-[#1A1A1A] border-[#27272A] text-white"><SelectValue placeholder="Selecione uma equipe" /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                          <SelectItem value="none" className="text-white">Nenhuma</SelectItem>
                          {teams.map((team) => (
                            <SelectItem key={team._id} value={team._id} className="text-white hover:bg-[#27272A]">{team.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]" htmlFor="athlete-professor">Professor (sensei)</Label>
                      <Select value={formData.professor_id || "none"} onValueChange={(v) => setFormData({ ...formData, professor_id: v === "none" ? "" : v })}>
                        <SelectTrigger id="athlete-professor" data-testid="athlete-professor" className="bg-[#1A1A1A] border-[#27272A] text-white"><SelectValue placeholder="Selecione um professor" /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                          <SelectItem value="none" className="text-white">Nenhum</SelectItem>
                          {filteredProfessorsForForm.map((p) => (
                            <SelectItem key={p._id} value={p._id} className="text-white hover:bg-[#27272A]">{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.team_id && filteredProfessorsForForm.length === 0 && (
                        <p className="text-xs text-yellow-400">Esta equipe ainda não tem professores cadastrados.</p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]" htmlFor="athlete-phone">Telefone</Label>
                      <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(00) 00000-0000" inputMode="tel" maxLength={20} id="athlete-phone" data-testid="athlete-phone" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]" htmlFor="athlete-email">Email</Label>
                      <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="atleta@email.com" id="athlete-email" data-testid="athlete-email" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]" htmlFor="athlete-start-date">Data de Início no BJJ</Label>
                      <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} id="athlete-start-date" data-testid="athlete-start-date" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]">Foto do Atleta</Label>
                      <ImageUpload entityType="athlete" entityId={editingAthlete?._id} currentPath={formData.photo_path} onUpload={handlePhotoUpload} shape="square" label="Upload Foto" />
                    </div>
                  </div>
                </div>

                {/* ============ SEÇÃO: RESPONSÁVEIS (colapsável) ============ */}
                <CollapsibleSection icon={<Users size={16} />} title="Responsáveis e Contato Estendido" testid="section-responsaveis">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-father">Pai</Label><Input value={formData.father_name} onChange={(e) => setFormData({ ...formData, father_name: e.target.value })} placeholder="Nome do pai" id="athlete-father" data-testid="athlete-father" className="bg-[#1A1A1A] border-[#27272A] text-white" /></div>
                    <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-mother">Mãe</Label><Input value={formData.mother_name} onChange={(e) => setFormData({ ...formData, mother_name: e.target.value })} placeholder="Nome da mãe" id="athlete-mother" data-testid="athlete-mother" className="bg-[#1A1A1A] border-[#27272A] text-white" /></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                    <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-home-phone">Tel. Residencial</Label><Input value={formData.home_phone} onChange={(e) => setFormData({ ...formData, home_phone: e.target.value })} placeholder="(00) 0000-0000" inputMode="tel" maxLength={20} id="athlete-home-phone" data-testid="athlete-home-phone" className="bg-[#1A1A1A] border-[#27272A] text-white" /></div>
                    <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-resp-cell">Celular do Responsável</Label><Input value={formData.responsible_cell_phone} onChange={(e) => setFormData({ ...formData, responsible_cell_phone: e.target.value })} placeholder="(00) 00000-0000" inputMode="tel" maxLength={20} id="athlete-resp-cell" data-testid="athlete-resp-cell" className="bg-[#1A1A1A] border-[#27272A] text-white" /></div>
                  </div>

                  {/* FASE E — RESPONSÁVEL LEGAL (obrigatório apenas para menores na
                      prática, mas todos os campos são opcionais no sistema). O CPF
                      do responsável pode repetir entre irmãos: é legítimo. */}
                  <div className="mt-4 pt-3 border-t border-[#27272A]" data-testid="athlete-guardian-block">
                    <p className="text-micro text-[#A1A1AA] font-semibold mb-2">
                      Responsável legal (recomendado para menores de 18 anos)
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[#A1A1AA]" htmlFor="athlete-guardian-name">Nome do responsável</Label>
                        <Input value={formData.guardian_name} onChange={(e) => setFormData({ ...formData, guardian_name: e.target.value })} placeholder="Nome completo" id="athlete-guardian-name" data-testid="athlete-guardian-name" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#A1A1AA]" htmlFor="athlete-guardian-relationship">Parentesco</Label>
                        <Select value={formData.guardian_relationship || 'none'} onValueChange={(v) => setFormData({ ...formData, guardian_relationship: v === 'none' ? '' : v })}>
                          <SelectTrigger id="athlete-guardian-relationship" data-testid="athlete-guardian-relationship" className="bg-[#1A1A1A] border-[#27272A] text-white"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                          <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                            <SelectItem value="none" className="text-white">—</SelectItem>
                            {GUARDIAN_RELATIONSHIPS.map((r) => (
                              <SelectItem key={r} value={r} className="text-white">{r}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="mt-3">
                      <CampoDocumento
                        tipo="cpf"
                        label="CPF do responsável"
                        value={formData.guardian_cpf}
                        onChange={(v) => setFormData({ ...formData, guardian_cpf: v })}
                        testId="athlete-guardian-cpf"
                        hint="Opcional. Pode repetir entre irmãos."
                      />
                    </div>
                    {avisoCpfResponsavel && (
                      <Dica tom="aviso" testid="athlete-guardian-same-cpf" className="mt-2">
                        {avisoCpfResponsavel}
                      </Dica>
                    )}
                  </div>
                </CollapsibleSection>

                {/* ============ SEÇÃO: ENDEREÇO (colapsável) ============ */}
                <CollapsibleSection icon={<MapPin size={16} />} title="Endereço" testid="section-endereco">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2 col-span-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-address">Endereço (Rua/Av e número)</Label><Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Rua Tal, 123" id="athlete-address" data-testid="athlete-address" className="bg-[#1A1A1A] border-[#27272A] text-white" /></div>
                    <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-zip">CEP</Label><Input value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })} placeholder="00000-000" inputMode="numeric" maxLength={9} id="athlete-zip" data-testid="athlete-zip" className="bg-[#1A1A1A] border-[#27272A] text-white" /></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
                    <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-neighborhood">Bairro</Label><Input value={formData.neighborhood} onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })} id="athlete-neighborhood" data-testid="athlete-neighborhood" className="bg-[#1A1A1A] border-[#27272A] text-white" /></div>
                    <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-city">Cidade</Label><Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} id="athlete-city" data-testid="athlete-city" className="bg-[#1A1A1A] border-[#27272A] text-white" /></div>
                    <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-state">Estado</Label><Input value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} placeholder="RJ" maxLength={2} id="athlete-state" data-testid="athlete-state" className="bg-[#1A1A1A] border-[#27272A] text-white" /></div>
                  </div>
                </CollapsibleSection>

                {/* ============ SEÇÃO: ESCOLA (colapsável) ============ */}
                <CollapsibleSection icon={<GraduationCap size={16} />} title="Dados Escolares" testid="section-escolar">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-escolaridade">Escolaridade</Label>
                      <Select value={formData.education_level || "none"} onValueChange={(v) => setFormData({ ...formData, education_level: v === "none" ? "" : v })}>
                        <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-white"id="athlete-escolaridade" ><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                          <SelectItem value="none" className="text-white">—</SelectItem>
                          <SelectItem value="Educação Infantil" className="text-white">Educação Infantil</SelectItem>
                          <SelectItem value="Fundamental" className="text-white">Fundamental</SelectItem>
                          <SelectItem value="Médio" className="text-white">Médio</SelectItem>
                          <SelectItem value="Técnico" className="text-white">Técnico</SelectItem>
                          <SelectItem value="Superior" className="text-white">Superior</SelectItem>
                          <SelectItem value="Pós-graduação" className="text-white">Pós-graduação</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-school">Escola</Label><Input value={formData.school_name} onChange={(e) => setFormData({ ...formData, school_name: e.target.value })} id="athlete-school" data-testid="athlete-school" className="bg-[#1A1A1A] border-[#27272A] text-white" /></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                    <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-grade">Série</Label><Input value={formData.school_grade} onChange={(e) => setFormData({ ...formData, school_grade: e.target.value })} placeholder="Ex: 5º ano" id="athlete-grade" data-testid="athlete-grade" className="bg-[#1A1A1A] border-[#27272A] text-white" /></div>
                    <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-turno">Turno</Label>
                      <Select value={formData.school_shift || "none"} onValueChange={(v) => setFormData({ ...formData, school_shift: v === "none" ? "" : v })}>
                        <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-white"id="athlete-turno" ><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                          <SelectItem value="none" className="text-white">—</SelectItem>
                          <SelectItem value="manha" className="text-white">Manhã</SelectItem>
                          <SelectItem value="tarde" className="text-white">Tarde</SelectItem>
                          <SelectItem value="noite" className="text-white">Noite</SelectItem>
                          <SelectItem value="integral" className="text-white">Integral</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CollapsibleSection>

                {/* ============ SEÇÃO: SAÚDE (colapsável) ============ */}
                <CollapsibleSection icon={<Heart size={16} />} title="Saúde" testid="section-saude">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <input type="checkbox" checked={!!formData.has_special_needs} onChange={(e) => setFormData({ ...formData, has_special_needs: e.target.checked })} className="w-4 h-4 accent-[#D4AF37]" id="athlete-has-special" data-testid="athlete-has-special" />
                      <Label className="text-white" htmlFor="athlete-has-special">Possui necessidades especiais?</Label>
                    </div>
                    {formData.has_special_needs && (
                      <Input value={formData.special_needs_description} onChange={(e) => setFormData({ ...formData, special_needs_description: e.target.value })} placeholder="Descreva..." data-testid="athlete-special-desc" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <input type="checkbox" checked={!!formData.has_health_issues} onChange={(e) => setFormData({ ...formData, has_health_issues: e.target.checked })} className="w-4 h-4 accent-[#D4AF37]" id="athlete-has-health" data-testid="athlete-has-health" />
                      <Label className="text-white" htmlFor="athlete-has-health">Possui algum problema de saúde?</Label>
                    </div>
                    {formData.has_health_issues && (
                      <Input value={formData.health_issues_description} onChange={(e) => setFormData({ ...formData, health_issues_description: e.target.value })} placeholder="Descreva..." data-testid="athlete-health-desc" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                    )}
                  </div>
                </CollapsibleSection>

                {/* ============ SEÇÃO: DADOS MÉDICOS (colapsável) ============ */}
                <CollapsibleSection icon={<Stethoscope size={16} />} title="Dados Médicos" testid="section-medico">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-tipo-sanguineo">Tipo Sanguíneo</Label>
                      <Select value={formData.blood_type || "none"} onValueChange={(v) => setFormData({ ...formData, blood_type: v === "none" ? "" : v })}>
                        <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-white"id="athlete-tipo-sanguineo" ><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                          <SelectItem value="none" className="text-white">—</SelectItem>
                          {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bt => <SelectItem key={bt} value={bt} className="text-white">{bt}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-height">Altura (cm)</Label><Input type="number" step="0.5" value={formData.height_cm} onChange={(e) => setFormData({ ...formData, height_cm: e.target.value })} placeholder="170" id="athlete-height" data-testid="athlete-height" className="bg-[#1A1A1A] border-[#27272A] text-white" /></div>
                    <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-cert-date">Data do Atestado Médico</Label><Input type="date" value={formData.medical_certificate_date} onChange={(e) => setFormData({ ...formData, medical_certificate_date: e.target.value })} id="athlete-cert-date" data-testid="athlete-cert-date" className="bg-[#1A1A1A] border-[#27272A] text-white" /></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                    <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-doctor">Nome do Médico</Label><Input value={formData.doctor_name} onChange={(e) => setFormData({ ...formData, doctor_name: e.target.value })} id="athlete-doctor" data-testid="athlete-doctor" className="bg-[#1A1A1A] border-[#27272A] text-white" /></div>
                    <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="athlete-crm">CRM</Label><Input value={formData.doctor_crm} onChange={(e) => setFormData({ ...formData, doctor_crm: e.target.value })} id="athlete-crm" data-testid="athlete-crm" className="bg-[#1A1A1A] border-[#27272A] text-white" /></div>
                  </div>
                </CollapsibleSection>

                {/* FASE 3 — HISTÓRICO DE GRADUAÇÃO (linha do tempo + registrar).
                    Só existe para atleta JÁ SALVO: o histórico é do atleta. */}
                <CollapsibleSection icon={<History size={16} />} title="Histórico de Graduação" testid="section-graduacao">
                  {editingAthlete ? (
                    <HistoricoGraduacao
                      athleteId={editingAthlete._id}
                      belt={formData.belt}
                      beltSince={formData.belt_since}
                      belts={belts}
                      onMudou={handleGraduacaoMudou}
                    />
                  ) : (
                    <p className="text-micro text-[#71717A]" data-testid="graduacao-novo-atleta">
                      Salve o atleta primeiro: o histórico de graduação é gravado por atleta.
                    </p>
                  )}
                </CollapsibleSection>
              </form>
              {/* Sticky footer */}
              <BarraAcoes contexto="dialogo">
                <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }} className="border-[#27272A] text-[#A1A1AA]">Cancelar</Button>
                <Button type="button" onClick={handleSubmit} disabled={saving} data-testid="save-athlete" className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingAthlete ? 'Salvar' : 'Criar')}
                </Button>
              </BarraAcoes>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* FASE 4 — RELATÓRIO DE COMPLETUDE: o que falta preencher, por dado,
            com o nome de quem está faltando (clique abre a ficha do atleta). */}
        <Dialog open={completudeOpen} onOpenChange={setCompletudeOpen}>
          <DialogContent className="bg-[#141414] border-[#27272A] w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white font-['Chivo']">Completude do cadastro</DialogTitle>
            </DialogHeader>
            <CompletudeCadastro
              onAbrirAtleta={(a) => {
                const atleta = athletes.find((x) => x._id === a.id);
                setCompletudeOpen(false);
                if (atleta) handleEdit(atleta);
              }}
            />
          </DialogContent>
        </Dialog>

        {/* Filtros em faixa própria (Etapa Visual 2) */}
        <FaixaFiltros rotulo="Buscar e filtrar">
        <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={20} />
            <Input placeholder="Buscar atletas..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="search-athletes" className="pl-10 bg-[#141414] border-[#27272A] text-white placeholder:text-[#71717A]" />
          </div>
          <div className="w-48">
            <Combobox
              value={beltFilter}
              onValueChange={(v) => { setBeltFilter(v); setPage(1); }}
              placeholder="Faixa"
              searchPlaceholder="Buscar faixa..."
              data-testid="filter-belt"
              options={[
                { value: 'all', label: 'Todas as Faixas' },
                ...belts.map((b) => ({ value: b.name, label: b.name, dotColor: b.color_code })),
              ]}
              renderLabel={(o) => (
                <span className="flex items-center gap-2 min-w-0">
                  {o.dotColor && (
                    <span
                      className="w-3 h-3 rounded-full inline-block shrink-0"
                      style={{
                        backgroundColor: o.dotColor,
                        border:
                          o.dotColor === '#000000'
                            ? '1px solid #555'
                            : o.dotColor === '#FFFFFF'
                              ? '1px solid #999'
                              : 'none',
                      }}
                    />
                  )}
                  <span className="truncate">{o.label}</span>
                </span>
              )}
            />
          </div>
          <div className="w-48">
            <Combobox
              value={teamFilter}
              onValueChange={(v) => {
                setTeamFilter(v);
                setPage(1);
                // Equipe escolhida = busca AQUELA equipe no servidor (uma por vez);
                // "Meus alunos" traz de volta o conjunto padrão.
                carregarAtletas(v === 'all' ? null : v);
              }}
              placeholder="Equipe"
              searchPlaceholder="Buscar equipe..."
              data-testid="filter-team"
              options={[
                // Para o professor, "sem filtro" é o modo "meus alunos" (regra 06/10)
                { value: 'all', label: isProfessor ? 'Meus alunos' : 'Todas as Equipes' },
                ...teams.map((t) => ({ value: t._id, label: t.name })),
              ]}
            />
          </div>
          {isProfessor && filtrandoPorEquipe && (
            /* VOLTA RÁPIDA (pedido do usuário): aparece só quando ele saiu da
               visão dos próprios alunos — um clique e volta. */
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setTeamFilter('all'); setPage(1); carregarAtletas(null); }}
              data-testid="filtro-meus-alunos"
              title="Voltar a ver somente os seus alunos"
              className="border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10"
            >
              <Users size={16} className="mr-1" /> Meus alunos
            </Button>
          )}
          <div className="w-48">
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger data-testid="filter-status" className="bg-[#141414] border-[#27272A] text-white"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="all" className="text-white">Todos os Status</SelectItem>
                <SelectItem value="approved" className="text-white">Aprovados</SelectItem>
                <SelectItem value="pending" className="text-white">Pendentes</SelectItem>
                <SelectItem value="rejected" className="text-white">Rejeitados</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* FASE 3 — "sem graduar há X": o limite é escolhido na hora (não é
              regra do sistema) e atleta SEM data de graduação entra na lista. */}
          <div className="w-52">
            <Select
              value={String(mesesSemGraduar)}
              onValueChange={(v) => { setMesesSemGraduar(Number(v)); setPage(1); }}
            >
              <SelectTrigger data-testid="filter-graduacao" className="bg-[#141414] border-[#27272A] text-white">
                <SelectValue placeholder="Graduação" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                {FILTROS_SEM_GRADUAR.map((f) => (
                  <SelectItem key={f.value} value={String(f.value)} className="text-white">
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        </FaixaFiltros>

        {/* Athletes Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>
        ) : filteredAthletes.length === 0 ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent>
              <EstadoVazio icone={UserCircle} titulo="Nenhum atleta encontrado" descricao="Cadastre seu primeiro atleta para comecar" />
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="bg-[#141414] border border-[#27272A] rounded-xl px-4">{renderPaginationBar(true)}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pagedAthletes.map((athlete) => (
              <Card key={athlete._id} className={`bg-[#141414] border-[#27272A] card-hover ${(athlete.status || 'approved') === 'pending' ? 'ring-1 ring-amber-500/40' : ''}`} data-testid={`athlete-card-${athlete._id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#1A1A1A] flex items-center justify-center text-[#D4AF37] font-bold text-lg">
                        {athlete.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-bold text-white">{athlete.name}</h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="px-2 py-0.5 rounded text-xs font-bold" style={getBeltStyle(athlete.belt)}>
                            {athlete.belt}
                          </span>
                          <span className="text-xs text-[#71717A]">{athlete.weight} kg</span>
                          {(athlete.status || 'approved') === 'pending' && (
                            <span className="px-2 py-0.5 rounded text-micro font-bold bg-amber-500/15 text-amber-400 border border-amber-500/40" data-testid={`athlete-status-pending-${athlete._id}`}>
                              PENDENTE
                            </span>
                          )}
                          {athlete.status === 'rejected' && (
                            <span className="px-2 py-0.5 rounded text-micro font-bold bg-red-500/15 text-red-400 border border-red-500/40">
                              REJEITADO
                            </span>
                          )}
                          {canManageAthlete(athlete) && !['super_admin', 'federation'].includes(user?.role) && (
                            <span
                              className="px-2 py-0.5 rounded text-micro font-bold bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40"
                              title="Atleta sob sua responsabilidade direta"
                              data-testid={`athlete-supervised-${athlete._id}`}
                            >
                              SOB MINHA SUPERVISÃO
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1 text-sm mb-4 text-[#A1A1AA]">
                    <p><strong className="text-[#71717A]">Categoria:</strong> {athlete.age_class || '-'}</p>
                    <p><strong className="text-[#71717A]">Equipe:</strong> {getTeamName(athlete.team_id)}</p>
                  </div>
                  {canApproveAthlete(athlete) && (
                    <div className="flex gap-2 mb-2">
                      <Button size="sm" onClick={() => handleApprove(athlete._id)} data-testid={`approve-athlete-${athlete._id}`} className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold">
                        Aprovar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleReject(athlete._id)} data-testid={`reject-athlete-${athlete._id}`} className="flex-1 border-red-500/40 text-red-400 hover:bg-red-500/10">
                        Rejeitar
                      </Button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Link to={`/athletes/${athlete._id}/card`} className="flex-1">
                      <Button variant="outline" size="sm" className="w-full border-[#27272A] text-[#A1A1AA] hover:text-white">
                        <CreditCard size={16} className="mr-1" /> Carteirinha
                      </Button>
                    </Link>
                    {/* FASE 4 — a ficha completa, para imprimir */}
                    <Link to={`/athletes/${athlete._id}/ficha`} data-testid={`ficha-athlete-${athlete._id}`}>
                      <Button variant="outline" size="sm" title="Ficha do atleta (imprimir)" className="border-[#27272A] text-[#A1A1AA] hover:text-white">
                        <Printer size={16} />
                      </Button>
                    </Link>
                    {canManage && (
                      <>
                        {canManageAthlete(athlete) && (
                          <Button variant="outline" size="sm" onClick={() => handleEdit(athlete)} data-testid={`edit-athlete-${athlete._id}`} className="border-[#27272A] text-[#A1A1AA] hover:text-white"><Edit size={16} /></Button>
                        )}
                        {canTransfer && canManageAthlete(athlete) && (
                          <Button variant="outline" size="sm" onClick={() => setTransferAthlete(athlete)} data-testid={`transfer-athlete-${athlete._id}`} title="Transferir equipe/professor" className="border-[#27272A] text-[#A1A1AA] hover:text-white"><ArrowRightLeft size={16} /></Button>
                        )}
                        {canSeeHistory(athlete) && (
                          <Button variant="outline" size="sm" onClick={() => setHistoryAthlete(athlete)} data-testid={`history-athlete-${athlete._id}`} title="Histórico de migrações" className="border-[#27272A] text-[#A1A1AA] hover:text-white"><History size={16} /></Button>
                        )}
                        {canDeleteAthlete && (
                          <>
                            {/* Separador (Fase 2, item 10): a exclusão fica afastada
                                das ações normais da linha. */}
                            <div className="w-px self-stretch bg-[#27272A] mx-1" aria-hidden="true" />
                            <Button variant="outline" size="sm" onClick={() => handleDelete(athlete._id)} data-testid={`delete-athlete-${athlete._id}`} className="border-[#27272A] text-red-400 hover:text-red-300"><Trash2 size={16} /></Button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            </div>
            <div className="bg-[#141414] border border-[#27272A] rounded-xl px-4">{renderPaginationBar(false)}</div>
          </>
        )}

        {/* Wave 3 — Transfer & history modals */}
        <TransferAthleteDialog
          athlete={transferAthlete}
          open={!!transferAthlete}
          onOpenChange={(v) => { if (!v) setTransferAthlete(null); }}
          currentUser={user}
          onDone={fetchData}
        />
        <MigrationHistoryDialog
          entity={historyAthlete}
          entityType="athlete"
          open={!!historyAthlete}
          onOpenChange={(v) => { if (!v) setHistoryAthlete(null); }}
        />

        {/* Fase 2 (item 7): mesmo efeito do prompt antigo — fechar por qualquer
            caminho (botão, X, Esc ou clique fora) confirma a rejeição. */}
        <DialogoMotivo
          aberto={!!rejeitando}
          titulo="Motivo da rejeição (opcional):"
          descricao="O atleta será rejeitado. O motivo é opcional e fica registrado."
          rotulo="Motivo (opcional)"
          placeholder="Ex.: documento do responsável ilegível"
          textoConfirmar="Rejeitar atleta"
          varianteConfirmar="destructive"
          soConfirmar
          aoConfirmar={(texto) => rejeitando && aplicarRejeicao(rejeitando, texto)}
          testId="dialogo-rejeitar-atleta"
        />
      </div>
    </MainLayout>
  );
}
